import { useEffect, useState } from "react";
import { fetchJson, ApiError } from "@podecontratar/shared/lib/api";
import { websiteOrigin } from "./lib/website-origin";

/**
 * Client-side auth gate for the admin SPA.
 *
 * The previous Next.js implementation used a server component (`admin/
 * layout.tsx`) to read the JWT cookie, verify it, and 403/redirect
 * before any HTML was sent. Vite/Pages doesn't have that hook — so we
 * do it on the client: a single `/api/auth/me` probe at boot, gated
 * on `email.endsWith("@zarv.com")` per the longstanding gate.
 *
 * Failure paths:
 *   - 401 / not authenticated → bounce to login on the website
 *     (`podecontratar.com.br/auth/login?redir=admin.podecontratar.com.br`)
 *   - 200 but email not @zarv.com → render a permission-denied card
 *   - 200 + @zarv.com → render the admin app
 *
 * Auth cookie is set on `.podecontratar.com.br` (Phase 2 backend
 * change), so a login on the marketing site carries here without
 * a separate sign-in form.
 */

type Me = {
  id: string;
  email: string;
  name?: string;
};


export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; user: Me }
    | { kind: "unauthorized"; email: string }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await fetchJson<Me>("/api/auth/me");
        if (cancelled) return;
        if (!user.email?.toLowerCase().endsWith("@zarv.com")) {
          setState({ kind: "unauthorized", email: user.email ?? "" });
          return;
        }
        setState({ kind: "ok", user });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          // Send the user to the website's login page; on success they
          // land back on admin.podecontratar.com.br.
          const redir = encodeURIComponent(window.location.href);
          window.location.href = `${websiteOrigin()}/auth/login?redir=${redir}`;
          return;
        }
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : "Erro ao validar acesso.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="grid min-h-screen place-items-center bg-surface text-text-secondary">
        <p className="text-sm tracking-tight">Validando acesso…</p>
      </div>
    );
  }

  if (state.kind === "unauthorized") {
    return (
      <PermissionDenied email={state.email} />
    );
  }

  if (state.kind === "error") {
    return (
      <div className="grid min-h-screen place-items-center bg-surface px-6 text-center">
        <div className="max-w-md">
          <h1 className="text-xl font-semibold tracking-tight text-text">
            Não consegui validar sua sessão.
          </h1>
          <p className="mt-3 text-sm text-text-secondary">{state.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex items-center rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-surface-alt"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function PermissionDenied({ email }: { email: string }) {
  return (
    <div className="grid min-h-screen place-items-center bg-surface px-6 text-center">
      <div className="max-w-md">
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-text-tertiary">
          Acesso restrito
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight text-text">
          Este painel é só pra equipe Zarv.
        </h1>
        <p className="mt-4 text-sm text-text-secondary">
          Você está logado(a) como <code className="font-mono text-xs">{email}</code>.
          Se precisa entrar com outra conta, sai e entra de novo na conta
          corporativa.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <a
            href={`${websiteOrigin()}/painel`}
            className="inline-flex items-center rounded-lg bg-accent px-4 py-2 text-sm font-medium text-text-inverse hover:bg-accent-hover"
          >
            Voltar pro painel
          </a>
          <a
            href={`${websiteOrigin()}/auth/logout`}
            className="inline-flex items-center rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-surface-alt"
          >
            Sair e trocar de conta
          </a>
        </div>
      </div>
    </div>
  );
}
